import React, {
  ChangeEvent,
  Fragment,
  useState,
  useEffect,
  useContext,
} from "react";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Select,
  MenuItem,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  Fab,
} from "@material-ui/core";
import {
  getLandmarkPoints,
  getMeters,
  getMeter,
  getLandmarkPoint,
} from "../apis/elastic";
import { DesignContext } from "../context/design";
import { MainContext } from "../context/main";
import { LangContext } from "../context/lang";
import MeterInfo from "./meter-info";
import ReactResizeDetector from "react-resize-detector";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "absolute",
    width: theme.spacing(48.75),
    top: theme.spacing(0.5),
    left: theme.spacing(0.5),
    zIndex: 10,
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    background: `#ffffffe6`,
    borderRadius: theme.spacing(3),
  },
  selectInput: {
    borderRadius: theme.spacing(2),
    "& .MuiOutlinedInput-inputMarginDense": {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px `,
      paddingRight: theme.spacing(3),
    },
  },
  textInput: {
    "& .MuiOutlinedInput-root": { borderRadius: theme.spacing(2) },
    "& .MuiOutlinedInput-inputMarginDense": {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px `,
    },
  },
  resultBox: {
    position: "absolute",
    width: theme.spacing(48.75),
    top: theme.spacing(5),
    left: theme.spacing(0.5),
    zIndex: 10,
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    background: `#ffffffe6`,
    borderRadius: theme.spacing(0.5),
  },
  clearButton: {
    textTransform: "unset",
    backgroundColor: theme.palette.error.main,
    height: `${theme.spacing(3)}px !important`,
    color: "#ffffff",
    "&:hover": { backgroundColor: theme.palette.error.dark },
  },
  flexCenter: {
    display: "flex",
    justifyContent: "center",
  },
}));

const SearchLocation = () => {
  const classes = useStyles();
  const [target, settarget] = useState<"meters" | "landmark-points">(
    "landmark-points"
  );
  const [textSearch, settextSearch] = useState<string>("");
  const [suggestion, setsuggestion] = useState<any>(undefined);
  const [loadInfo, setloadInfo] = useState<boolean>(false);
  const { map } = useContext(DesignContext);
  const { userInfo } = useContext(MainContext);
  const { lang } = useContext(LangContext);
  const [resultMaxHeight, setresultMaxHeight] = useState<number>(0);

  const changeTarget = (event: React.ChangeEvent<{ value: unknown }>) => {
    settarget(event.target.value as typeof target);
  };

  useEffect(() => {
    const abortControl = new AbortController();
    if (textSearch.length > 2) {
      setsuggestion("loadding");
      setTimeout(() => {
        target === "landmark-points"
          ? getLandmarkPoints(textSearch, abortControl)
              .then((rs) => setsuggestion(rs))
              .catch((error) => {
                console.log(error);
                setsuggestion([]);
              })
          : getMeters(textSearch, abortControl)
              .then((rs) => setsuggestion(rs))
              .catch((error) => {
                console.log(error);
                setsuggestion([]);
              });
      }, 1000);
    }
    return () => {
      if (textSearch.length > 2) {
        abortControl.abort();
        setsuggestion(undefined);
      }
    };
  }, [textSearch, target]);

  const clearSearch = () => {
    settextSearch("");
    setsuggestion(undefined);
    map?.getLayer("hilight-meter-search") &&
      map.removeLayer("hilight-meter-search");
    map?.getSource("hilight-meter-search") &&
      map.removeSource("hilight-meter-search");
  };

  const _clickInfo = (sg: any) => async () => {
    setloadInfo(true);
    const rs: any =
      target === "meters"
        ? await getMeter(sg._id)
        : await getLandmarkPoint(sg._id);

    setloadInfo(false);
    map?.flyTo({
      center: [Number(rs.lat), Number(rs.lon)],
      zoom: 19.333333333,
    });
  };

  return (
    <Fragment>
      <ReactResizeDetector
        handleHeight
        onResize={(w, h) => {
          setresultMaxHeight(h);
        }}
      />
      <div className={classes.root}>
        <Grid container spacing={1}>
          {userInfo?.role !== "customer" && (
            <Grid item xs={5}>
              <Select
                margin="dense"
                fullWidth
                className={classes.selectInput}
                variant="outlined"
                value={target}
                onChange={changeTarget}
              >
                <MenuItem value={"meters"}>{lang.data.electricUser}</MenuItem>
                <MenuItem value={"landmark-points"}>
                  {lang.data.landmarkPoint}
                </MenuItem>
              </Select>
            </Grid>
          )}
          <Grid
            item
            xs={userInfo?.role === "customer" ? 12 : 7}
            className={classes.flexCenter}
            style={{
              flexDirection: "column",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              className={classes.textInput}
              value={textSearch}
              style={{ marginTop: 0, marginBottom: 0 }}
              margin="dense"
              placeholder={
                userInfo?.role === "customer" ? lang.data.landmarkPoint : ""
              }
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                settextSearch(event.target.value);
              }}
            />
          </Grid>
        </Grid>
      </div>
      {suggestion && (
        <div className={classes.resultBox}>
          {typeof suggestion === "object" && suggestion.length > 0 ? (
            <Fragment>
              <List
                component="nav"
                dense
                style={{ maxHeight: resultMaxHeight - 180, overflowY: "auto" }}
              >
                {suggestion.map((sg: any) =>
                  sg.meter_no ? (
                    // <ListItem
                    //   button
                    //   key={sg._id}
                    //   onClick={_clickInfo(sg)}
                    //   disabled={loadInfo}
                    // >
                    //   <ListItemText
                    //     primary={sg.meter_no}
                    //     secondary={`${sg.name} ${sg.sur_name} ${sg.house_no} ${sg.moo_village} ${sg.soi} ${sg.street} ${sg.sub_distinct} ${sg.district} ${sg.province} ${sg.postcode}`}
                    //   />
                    // </ListItem>
                    <MeterInfo
                      key={sg._id}
                      sg={sg}
                      loadInfo={loadInfo}
                      setloadInfo={setloadInfo}
                    />
                  ) : (
                    <ListItem
                      button
                      key={sg._id}
                      disabled={loadInfo}
                      onClick={_clickInfo(sg)}
                    >
                      <ListItemText
                        primary={sg.name}
                        secondary={sg.road_name}
                      />
                    </ListItem>
                  )
                )}
              </List>
              <div className={classes.flexCenter}>
                <Fab
                  variant="extended"
                  size="small"
                  className={classes.clearButton}
                  onClick={clearSearch}
                >
                  {lang.data.clear}
                </Fab>
              </div>
            </Fragment>
          ) : typeof suggestion === "object" && suggestion.length === 0 ? (
            <div className={classes.flexCenter}>
              <Typography variant="caption" component="p">
                No Data
              </Typography>
            </div>
          ) : (
            <div className={classes.flexCenter}>
              <Typography variant="caption" component="p">
                Loading...
              </Typography>
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default SearchLocation;
